/**
 * Headless Solar Hijri calendar state (FND-19): month grid keyboard, range selection, presets.
 * Grid geometry comes from `@saay/formatting` (`buildSolarMonthGrid`).
 */

import {
  buildSolarMonthGrid,
  type LocalDate,
  localDateToSolarHijri,
  parseLocalDate,
  parseSolarHijriInput,
  solarHijriToLocalDate,
  todayBusinessDate,
  WEEKDAYS_FA,
} from '@saay/formatting';

/** Weekday column headers with Saturday first (material-standard §3.5). */
export const SOLAR_WEEKDAY_HEADERS_FA: readonly string[] = [
  WEEKDAYS_FA[6],
  WEEKDAYS_FA[0],
  WEEKDAYS_FA[1],
  WEEKDAYS_FA[2],
  WEEKDAYS_FA[3],
  WEEKDAYS_FA[4],
  WEEKDAYS_FA[5],
];

export type CalendarPanelView = 'day' | 'month' | 'year';

export function compareLocalDate(left: LocalDate, right: LocalDate): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function addLocalDays(value: LocalDate, days: number): LocalDate {
  const next = parseLocalDate(value).add({ days });
  return `${String(next.year).padStart(4, '0')}-${String(next.month).padStart(2, '0')}-${String(next.day).padStart(2, '0')}`;
}

export function shiftSolarMonth(
  year: number,
  month: number,
  deltaMonths: number,
): { readonly month: number; readonly year: number } {
  let nextMonth = month + deltaMonths;
  let nextYear = year;
  while (nextMonth < 1) {
    nextMonth += 12;
    nextYear -= 1;
  }
  while (nextMonth > 12) {
    nextMonth -= 12;
    nextYear += 1;
  }
  return { month: nextMonth, year: nextYear };
}

export function solarMonthBounds(
  year: number,
  month: number,
): { readonly end: LocalDate; readonly start: LocalDate } {
  const grid = buildSolarMonthGrid({ month, year });
  const inMonth = grid.weeks.flat().filter((cell) => cell.inMonth);
  const start = inMonth[0]?.localDate;
  const end = inMonth[inMonth.length - 1]?.localDate;
  if (start === undefined || end === undefined) {
    throw new RangeError(`Not a valid Solar Hijri month: ${year}/${month}`);
  }
  return { end, start };
}

export function localDateFromSolarDigits(raw: string): LocalDate | null {
  if (raw.length !== 8) {
    return null;
  }
  return parseSolarHijriInput(`${raw.slice(0, 4)}/${raw.slice(4, 6)}/${raw.slice(6, 8)}`);
}

export function solarDigitsFromLocalDate(value: LocalDate): string {
  const solar = localDateToSolarHijri(value);
  return `${String(solar.year).padStart(4, '0')}${String(solar.month).padStart(2, '0')}${String(solar.day).padStart(2, '0')}`;
}

export function visibleMonthForValue(
  value: LocalDate | '',
  min: LocalDate | undefined,
  today: LocalDate,
): { readonly month: number; readonly year: number } {
  if (value !== '') {
    const solar = localDateToSolarHijri(value);
    return { month: solar.month, year: solar.year };
  }
  if (min !== undefined) {
    const solar = localDateToSolarHijri(min);
    return { month: solar.month, year: solar.year };
  }
  const solar = localDateToSolarHijri(today);
  return { month: solar.month, year: solar.year };
}

export function moveFocusedDay(focused: LocalDate, key: string): LocalDate | null {
  const deltas: Record<string, number> = {
    ArrowDown: 7,
    ArrowLeft: 1,
    ArrowRight: -1,
    ArrowUp: -7,
    PageDown: 0,
    PageUp: 0,
  };
  if (key === 'PageUp') {
    const solar = localDateToSolarHijri(focused);
    const prev = shiftSolarMonth(solar.year, solar.month, -1);
    const bounds = solarMonthBounds(prev.year, prev.month);
    const day = Math.min(localDateToSolarHijri(focused).day, localDateToSolarHijri(bounds.end).day);
    return solarHijriToLocalDate({ day, month: prev.month, year: prev.year });
  }
  if (key === 'PageDown') {
    const solar = localDateToSolarHijri(focused);
    const next = shiftSolarMonth(solar.year, solar.month, 1);
    const bounds = solarMonthBounds(next.year, next.month);
    const day = Math.min(localDateToSolarHijri(focused).day, localDateToSolarHijri(bounds.end).day);
    return solarHijriToLocalDate({ day, month: next.month, year: next.year });
  }
  const delta = deltas[key];
  if (delta === undefined) {
    return null;
  }
  return addLocalDays(focused, delta);
}

export function isBetweenInclusive(value: LocalDate, start: LocalDate, end: LocalDate): boolean {
  return compareLocalDate(value, start) >= 0 && compareLocalDate(value, end) <= 0;
}

export function selectRangeClick(
  start: LocalDate | '',
  end: LocalDate | '',
  clicked: LocalDate,
): { readonly end: LocalDate | ''; readonly start: LocalDate } {
  if (start === '' || (start !== '' && end !== '')) {
    return { end: '', start: clicked };
  }
  if (compareLocalDate(clicked, start) < 0) {
    return { end: start, start: clicked };
  }
  return { end: clicked, start };
}

export type DateRangePresetId =
  'today' | 'yesterday' | 'last7' | 'thisWeek' | 'thisMonth' | 'prevMonth';

export interface DateRangePreset {
  readonly end: LocalDate;
  readonly id: DateRangePresetId;
  readonly label: string;
  readonly start: LocalDate;
}

export function dateRangePresets(
  today: LocalDate = todayBusinessDate(),
): readonly DateRangePreset[] {
  const solar = localDateToSolarHijri(today);
  const thisMonth = solarMonthBounds(solar.year, solar.month);
  const prev = shiftSolarMonth(solar.year, solar.month, -1);
  const prevMonth = solarMonthBounds(prev.year, prev.month);
  const day = parseLocalDate(today);
  const weekday = new Date(Date.UTC(day.year, day.month - 1, day.day)).getUTCDay();
  const saturday = day.subtract({ days: (weekday + 1) % 7 });
  const friday = saturday.add({ days: 6 });
  const weekStart = `${String(saturday.year).padStart(4, '0')}-${String(saturday.month).padStart(2, '0')}-${String(saturday.day).padStart(2, '0')}`;
  const weekEnd = `${String(friday.year).padStart(4, '0')}-${String(friday.month).padStart(2, '0')}-${String(friday.day).padStart(2, '0')}`;

  return [
    { end: today, id: 'today', label: 'امروز', start: today },
    {
      end: addLocalDays(today, -1),
      id: 'yesterday',
      label: 'دیروز',
      start: addLocalDays(today, -1),
    },
    { end: today, id: 'last7', label: '۷ روز اخیر', start: addLocalDays(today, -6) },
    { end: weekEnd, id: 'thisWeek', label: 'این هفته', start: weekStart },
    { end: thisMonth.end, id: 'thisMonth', label: 'این ماه', start: thisMonth.start },
    { end: prevMonth.end, id: 'prevMonth', label: 'ماه قبل', start: prevMonth.start },
  ];
}

export function rangeDayCount(start: LocalDate, end: LocalDate): number {
  let count = 0;
  let cursor = start;
  while (compareLocalDate(cursor, end) <= 0) {
    count += 1;
    if (cursor === end) {
      break;
    }
    cursor = addLocalDays(cursor, 1);
  }
  return count;
}

export function isDayDisabled(
  localDate: LocalDate,
  min: LocalDate | undefined,
  max: LocalDate | undefined,
): boolean {
  if (min !== undefined && compareLocalDate(localDate, min) < 0) {
    return true;
  }
  if (max !== undefined && compareLocalDate(localDate, max) > 0) {
    return true;
  }
  return false;
}

export function clampRangeEndToMaxSpan(
  start: LocalDate,
  end: LocalDate,
  maxSpanDays: number | undefined,
): LocalDate {
  if (maxSpanDays === undefined || maxSpanDays < 1) {
    return end;
  }
  const latest = addLocalDays(start, maxSpanDays - 1);
  return compareLocalDate(end, latest) > 0 ? latest : end;
}

export { buildSolarMonthGrid };
